import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../app/store";
import { CarreraItem, FechasPrioridad, FlagsSesion } from "../types";
import { updatePrioridadBuilder } from "../reduxAsyncThunks/prioridad.thunks";
import { doLoginStudentBuilder } from "../reduxAsyncThunks/login.thunks";
import { SessionSnapshot } from "../utils/sessionSnapshot";

export interface StudentState {
  id: number;
  email: string;
  completeName: { name: string; lastName: string };
  studentInitState: number;
  bloqueoActivo: boolean;
  bloqueoMensaje: string;
  bloqueoSubmensaje: string;
  prioridad: boolean | null;
  mensajePrioridad: string;
  fechasPrioridad: FechasPrioridad;
  mostrarCountdownPrioridad: boolean;
  /** Hora de servidor menos hora local, medida al recibir la última respuesta de prioridad. */
  serverClockOffsetMs: number;
  actualizandoPrioridad: boolean;
  debeConsultarCupos: boolean;
  mostrarHorarios: boolean;
  mostrarAulas: boolean;
  carreraActual: string;
  listadoCarreras: CarreraItem[];
  token: string;
  debeAlertarSuperposicion: boolean;
  debeFiltrarCursosConCupoNoAsignado: boolean;
}

const initialState: StudentState = {
  id: 0,
  email: "",
  completeName: { name: "", lastName: "" },
  studentInitState: 0,
  bloqueoActivo: false,
  bloqueoMensaje: "",
  bloqueoSubmensaje: "",
  prioridad: null,
  mensajePrioridad: "Revisando prioridad.",
  fechasPrioridad: { F_DESDE: "", F_HASTA: "" },
  mostrarCountdownPrioridad: false,
  serverClockOffsetMs: 0,
  mostrarHorarios: true,
  mostrarAulas: true,
  actualizandoPrioridad: false,
  debeConsultarCupos: false,
  carreraActual: localStorage.getItem("carreraActual") ?? "",
  listadoCarreras: [],
  token: "",
  debeAlertarSuperposicion: false,
  debeFiltrarCursosConCupoNoAsignado: false,
};

export const studentSlice = createSlice({
  name: "student",
  initialState,
  reducers: {
    updateStudentData: (state, action) => {
      state.id = action.payload.id;
      state.email = state.email || action.payload.email;
      state.completeName = action.payload.completeName;
    },
    setStudentInicializado: (state, action: PayloadAction<number>) => {
      state.studentInitState = action.payload;
    },
    setStudentBloqueoActivo: (state, action) => {
      state.bloqueoActivo = action.payload;
    },
    setStudentBloqueoMensajes: (state, action) => {
      state.bloqueoMensaje = action.payload.mensaje;
      state.bloqueoSubmensaje = action.payload.subMensaje;
    },
    setStudentPrioridad: (state, action) => {
      if (typeof action.payload.prioridad === "boolean") {
        state.prioridad = action.payload.prioridad;
      }
      state.mensajePrioridad = action.payload.mensajePrioridad;
      state.actualizandoPrioridad = !!action.payload.actualizandoPrioridad;
      state.fechasPrioridad = action.payload.fechasPrioridad;
      state.mostrarCountdownPrioridad = !!action.payload.debeMostrarContador;
    },
    setStudentMostrarHorarios: (state, { payload }) => {
      state.mostrarHorarios = payload;
    },
    setStudentMostrarAulas: (state, { payload }: PayloadAction<boolean>) => {
      state.mostrarAulas = payload;
    },
    setCarrearActual: (state, { payload }) => {
      const value = payload.trim();
      state.carreraActual = value;
      if (value) {
        localStorage.setItem("carreraActual", value);
      } else {
        localStorage.removeItem("carreraActual");
      }
    },
    setToken: (state, { payload }) => {
      state.token = payload;
    },
    setListadoCarreras: (state, { payload }) => {
      state.listadoCarreras = payload;
    },
    setDebeAlertarSuperposicion: (state, { payload }) => {
      state.debeAlertarSuperposicion = payload;
    },
    setFlagsSesion: (state, action: PayloadAction<FlagsSesion>) => {
      state.mostrarAulas = action.payload.mostrarAulas;
      state.debeAlertarSuperposicion = action.payload.debeAlertarSuperposicion;
      state.debeFiltrarCursosConCupoNoAsignado = action.payload.filtrarCursosConCupoNoAsignado;
    },
    hydrateStudentSession: (state, action: PayloadAction<SessionSnapshot>) => {
      const snapshot = action.payload;
      state.token = snapshot.token;
      state.id = snapshot.id;
      state.email = snapshot.email;
      state.completeName = snapshot.completeName ?? { name: "", lastName: "" };
      state.studentInitState = snapshot.studentInitState;
      state.listadoCarreras = snapshot.listadoCarreras ?? [];
      state.carreraActual = snapshot.carreraActual ?? "";
      state.mostrarHorarios = snapshot.mostrarHorarios ?? true;
      state.mostrarAulas = snapshot.mostrarAulas ?? true;
      state.bloqueoActivo = !!snapshot.bloqueoActivo;
      state.bloqueoMensaje = snapshot.bloqueoMensaje ?? "";
      state.bloqueoSubmensaje = snapshot.bloqueoSubmensaje ?? "";
      state.debeAlertarSuperposicion = !!snapshot.debeAlertarSuperposicion;
      state.debeFiltrarCursosConCupoNoAsignado = !!snapshot.debeFiltrarCursosConCupoNoAsignado;
      if (typeof snapshot.debeConsultarCupos === "boolean") {
        state.debeConsultarCupos = snapshot.debeConsultarCupos;
      }
    },
  },
  extraReducers(builder) {
    updatePrioridadBuilder(builder);
    doLoginStudentBuilder(builder);
  },
});

export const selectStudent = (state: RootState) => state.student;
export const selectToken = (state: RootState) => state.student.token;
export const selectListadoCarreras = (state: RootState) => state.student.listadoCarreras;
export const selectDebeAlertarSuperposicion = (state: RootState) => state.student.debeAlertarSuperposicion;
export const selectDebeConsultarCupos = (state: RootState) => state.student.debeConsultarCupos;

export const {
  updateStudentData,
  setStudentInicializado,
  setStudentBloqueoActivo,
  setStudentBloqueoMensajes,
  setStudentPrioridad,
  setStudentMostrarHorarios,
  setStudentMostrarAulas,
  setCarrearActual,
  setToken,
  setListadoCarreras,
  setDebeAlertarSuperposicion,
  setFlagsSesion,
  hydrateStudentSession,
} = studentSlice.actions;

// Exportar el thunk
export { updatePrioridadThunk } from "../reduxAsyncThunks/prioridad.thunks";
export { doLoginThunk } from "../reduxAsyncThunks/login.thunks";

export default studentSlice.reducer;
